import {
  Box,
  Text,
  Card,
  Grid,
  Badge,
  Group,
  Image,
  UnstyledButton,
} from '@mantine/core'
import { IconSchool } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import {
  learningEntries,
  type LearningCategory,
  type LearningEntry,
} from '../../content'
import databricksIcon from '../../assets/databricks_icon.png'
import dbtIcon from '../../assets/dbt_icon.png'

const CATEGORY_META: Record<
  LearningCategory,
  { label: string; color: string; logoSrc: string }
> = {
  dbt: { label: 'dbt', color: 'orange', logoSrc: dbtIcon },
  databricks: { label: 'Databricks', color: 'red', logoSrc: databricksIcon },
}

function LearningModuleCard({ entry }: { entry: LearningEntry }) {
  const navigate = useNavigate()
  const meta = CATEGORY_META[entry.category]

  return (
    <UnstyledButton
      onClick={() => navigate(entry.path)}
      style={{ width: '100%', textDecoration: 'none' }}
    >
      <Card
        shadow="sm"
        withBorder
        p="lg"
        style={{
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          height: '100%',
        }}
      >
        <Group mb="sm">
          <Box bg={`${meta.color}.0`} p={6} style={{ borderRadius: '8px' }}>
            <Image
              src={meta.logoSrc}
              alt={`${meta.label} logo`}
              w={20}
              h={20}
            />
          </Box>
        </Group>
        <Text size="md" fw={600} c="gray.9" mb="xs">
          {entry.title}
        </Text>
        <Text size="sm" c="gray.6" lineClamp={3}>
          {entry.description}
        </Text>
      </Card>
    </UnstyledButton>
  )
}

function LearningCategorySection({
  category,
  entries,
}: {
  category: LearningCategory
  entries: LearningEntry[]
}) {
  const meta = CATEGORY_META[category]
  if (!entries.length) return null

  return (
    <Box mb="xl">
      <Group mb="md">
        <Text size="lg" fw={700} c="gray.9">
          {meta.label}
        </Text>
        <Badge size="sm" color={meta.color} variant="light">
          {entries.length} module{entries.length === 1 ? '' : 's'}
        </Badge>
      </Group>
      <Grid>
        {entries.map((entry) => (
          <Grid.Col span={{ base: 12, md: 6 }} key={entry.slug}>
            <LearningModuleCard entry={entry} />
          </Grid.Col>
        ))}
      </Grid>
    </Box>
  )
}

export function Learning() {
  const dbtEntries = learningEntries.filter((e) => e.category === 'dbt')
  const databricksEntries = learningEntries.filter(
    (e) => e.category === 'databricks'
  )

  return (
    <Box>
      <Box mb="xl">
        <Group gap="xs" mb="md">
          <IconSchool size={28} color="var(--mantine-color-prism-6)" />
          <Text size="h1" fw={700} c="gray.9">
            Learning
          </Text>
        </Group>
        <Text size="lg" c="gray.6">
          Self-paced modules covering dbt and Databricks concepts as they're
          used across the Prism platform.
        </Text>
      </Box>

      <LearningCategorySection category="dbt" entries={dbtEntries} />
      <LearningCategorySection
        category="databricks"
        entries={databricksEntries}
      />
    </Box>
  )
}
